import React, { useCallback, useEffect, useState } from 'react';
import { FileError, useDropzone } from 'react-dropzone';
import {
  AppButton,
  Box,
  Inscription,
  NavigationLeftIcon,
  Preloader,
  RawButton,
  RequiredIcon,
  Text,
  TextAreaInput,
  useResponsive,
} from '@common-fe/common-fe';
import { get } from 'lodash';

import { ACCEPTED_FILE_TYPES, FileImage, MAX_SIZE_OF_FILE, SPECIFIC_FIELD_REQUIRED_TEXT } from '@/common/constants';
import regexp from '@/common/regexp';
import { Modal, SuccessModal } from '@/components';
import { FileErrorCode, ServerFileErrorMessage } from '@/components/controls/DropScan/dropScan.types';
import ScanPreviewContainer from '@/components/controls/DropScan/ScanPreview/ScanPreviewContainer';
import UploadFileErrorPopup from '@/components/controls/DropScan/UploadFileErrorPopup';
import useFilesInDocumentStore, { clearFilesInDocumentStore } from '@/components/controls/DropScan/useFilesInDocument.store';
import useOnDrop from '@/components/controls/DropScan/useOnDrop';
import { TransactionDocumentsPreviewModal } from '@/modules/transaction/components/TransactionDetails/TransactionDocumentsPreview/TransactionDocumentsPreviewModal';
import useSubmitClaimAppealQuery from '@/modules/transaction/queries/useSubmitClaimAppeal.query';
import useUploadAppealDocumentQuery from '@/modules/transaction/queries/useUploadAppealDocument.query';

const APPEAL_STORE_KEY = 'claimAppeal';

import { AppealFileItem } from './AppealFileItem';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  claimId?: string;
}

const ClaimAppealModal: React.FC<Props> = ({ isOpen, onClose, claimId }) => {
  const { isMobile } = useResponsive();
  const [description, setDescription] = useState('');
  const [hasSubmitAttempted, setHasSubmitAttempted] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [files, setFiles] = useState<FileImage[]>([]);
  const [uploadError, setUploadError] = useState<FileError | undefined>();
  const [previewFileIndex, setPreviewFileIndex] = useState<number | null>(null);

  const { handleSetValues: setAppealStoreFiles } = useFilesInDocumentStore(APPEAL_STORE_KEY);

  useEffect(() => {
    setAppealStoreFiles({ files });
  }, [files, setAppealStoreFiles]);

  const { uploadAppealDocument, isLoading: isUploadLoading } = useUploadAppealDocumentQuery();
  const { submitClaimAppeal, isLoading: isSubmitLoading } = useSubmitClaimAppealQuery();
  const isLoading = isUploadLoading || isSubmitLoading;

  const { onDrop } = useOnDrop({
    onFilesAdded: (newFiles) => setFiles((prev) => [...prev, ...newFiles]),
    onFileUploadError: (error) => setUploadError(error),
    disableFilesUpload: true,
  });
  const { getInputProps, open } = useDropzone({
    accept: ACCEPTED_FILE_TYPES,
    maxSize: MAX_SIZE_OF_FILE,
    onDrop,
    noDrag: true,
    noClick: true,
  });

  const onDescriptionChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>) => {
      const value = event.target.value;
      const withoutNewlines = value.replace(regexp.NEWLINE_SYMBOL, '');
      if (withoutNewlines.length > 1000) return;
      setDescription(value);
    },
    []
  );

  const descriptionWithoutNewlines = description.replace(regexp.NEWLINE_SYMBOL, '');
  const isDescriptionEmpty = !descriptionWithoutNewlines.trim();
  const showDescriptionError = hasSubmitAttempted && isDescriptionEmpty;

  const descriptionTexts = (
    <>
      <Inscription size="small" color="textSecondary">
        Request for a decision reversal must be submitted within 180 days of the date of the original denial.
      </Inscription>
      <Box margin={{ top: 'spacing12' }}>
        <Inscription size="small" color="textSecondary">
          Submit additional information related to your claim as part of the appeal. Documents can include written
          comments, medical records, letter of medical necessity from your healthcare provider, and any other
          information you feel will support a decision reversal.
        </Inscription>
      </Box>
      <Box margin={{ top: 'spacing12' }}>
        <Inscription size="small" color="textSecondary">
          Response to your appeal can take up to 30 days.
        </Inscription>
      </Box>
    </>
  );

  const formContent = (
    <Box
      background="canvas"
      round="container1Round"
      border={{ size: 'small', color: 'border2' }}
      pad="spacing24"
    >
      <Text weight="bold" color="textBody">
        <RequiredIcon color="danger" size="8px" style={{ marginRight: '2px', marginTop: '4px' }} />{' '}Description
      </Text>
      <Box margin={{ top: 'spacing4' }}>
        <Inscription size="small" color="textSecondary">
          Provide a brief summary of why you think your claim (or any part of the claim) should be approved.
        </Inscription>
      </Box>
      <Box margin={{ top: 'spacing12' }} data-testid="claim-appeal-description-wrapper">
        <TextAreaInput
          style={{ padding: '10px 16px', resize: 'none', ...(isMobile ? { minHeight: '203px' } : {}) }}
          data-testid="claim-appeal-description"
          placeholder="Enter appeal description"
          value={description}
          onChange={onDescriptionChange}
        />
        <Box margin={{ top: 'spacing4' }}>
          {showDescriptionError ? (
            <Inscription size="small" color="danger" style={{ lineHeight: '18px' }}>
              {SPECIFIC_FIELD_REQUIRED_TEXT('Description')}
            </Inscription>
          ) : (
            <Inscription size="small" weight={500} color="textBody" style={{ lineHeight: '18px' }}>
              {descriptionWithoutNewlines.length}/1000
            </Inscription>
          )}
        </Box>
      </Box>

      <Box
        direction={isMobile ? 'column' : 'row'}
        justify="between"
        align={isMobile ? 'start' : 'center'}
        margin={{ top: 'spacing16' }}
      >
        <Box flex>
          <Text weight="bold" color="textBody">Additional documentation</Text>
          <Box margin={{ top: 'spacing4' }}>
            <Inscription size="small" color="textSecondary">
              Any additional information that may be submitted with your appeal form include additional receipt(s),
              letter of medical necessity, or any other relevant details.
            </Inscription>
          </Box>
        </Box>
        <Box
          fill={isMobile ? 'horizontal' : undefined}
          margin={{ left: isMobile ? undefined : 'spacing24', top: isMobile ? 'spacing12' : undefined }}
        >
          <AppButton
            buttonType="secondary"
            fill={isMobile}
            {...(isMobile ? { style: { padding: '16px 10px' } } : { width: '160px' })}
            onClick={open}
            testId="claim-appeal-upload"
          >
            Upload
          </AppButton>
          <input {...getInputProps()} />
        </Box>
      </Box>

      {files.length > 0 && (
        <Box margin={{ top: 'spacing16' }} data-testid="appeal-files-list">
          <Inscription size="small" weight={500} color="textBody" style={{ lineHeight: '18px' }}>
            Uploaded:
          </Inscription>
          {files.map((file, index) => (
            <AppealFileItem
              key={file.temporalId}
              file={file}
              onClick={() => setPreviewFileIndex(index)}
              onRemove={(removed) => setFiles((prev) => prev.filter((f) => f.temporalId !== removed.temporalId))}
            />
          ))}
        </Box>
      )}
    </Box>
  );

  const handleClose = () => {
    if (previewFileIndex !== null) {
      setPreviewFileIndex(null);
      return;
    }
    clearFilesInDocumentStore(APPEAL_STORE_KEY);
    setDescription('');
    setHasSubmitAttempted(false);
    setFiles([]);
    setUploadError(undefined);
    onClose();
  };

  const handleSubmit = async () => {
    setHasSubmitAttempted(true);
    if (isDescriptionEmpty || !claimId) return;

    let uploadResults;
    try {
      uploadResults = await Promise.all(files.map((file) => uploadAppealDocument({ claimId, file })));
    } catch (error) {
      const errorMessage = get(error, 'response.data.elevate_error_message', '') as string;
      if (
        errorMessage?.includes(ServerFileErrorMessage.PasswordProtectedDocument) ||
        errorMessage?.includes(ServerFileErrorMessage.PasswordProtectedFile)
      ) {
        setUploadError({ code: FileErrorCode.PasswordProtectedDocument, message: '' });
        return;
      }
      setUploadError({ code: '', message: '' });
      return;
    }

    const documentFilePaths = uploadResults
      .map((res) => res.data.document_file_path)
      .filter(Boolean) as string[];
    await submitClaimAppeal({ claimId, description, documentFilePaths });
    setIsSuccess(true);
  };

  if (isSuccess) {
    return (
      <SuccessModal
        header="Claim Appeal Submitted!"
        buttonText="Got It!"
        buttonStyle={{ width: '160px' }}
        visible
        onSetVisible={() => {
          setIsSuccess(false);
          handleClose();
        }}
      />
    );
  }

  return (
    <Modal
      visible={isOpen}
      onSetVisible={handleClose}
      testId="claim-appeal-modal"
      isCloseButtonHidden={isMobile}
      isFullScreen={isMobile}
      contentMobileStyle={{ padding: 0 }}
      isClosingByClickOutsideBlocked={previewFileIndex !== null || !!uploadError}
      {...(!isMobile ? { contentStyle: { maxWidth: '720px' } } : {})}
    >
      {isMobile ? (
        <>
          <Box pad={{ top: 'spacing24', right: 'spacing8', left: 'spacing10' }}>
            <RawButton onClick={handleClose} data-testId="claim-appeal-modal_back-button">
              <Box direction="row" align="center">
                <Box margin={{ left: 'spacing10' }}>
                  <NavigationLeftIcon color="textAccent" size="20px" />
                </Box>
                <Text color="textAccent" size="small" weight={700} margin={{ left: 'spacing20' }}>
                  Claim Appeal
                </Text>
              </Box>
            </RawButton>
          </Box>

          <Box pad={{ top: 'spacing12', horizontal: 'spacing16' }}>
            {previewFileIndex !== null && (
              <ScanPreviewContainer
                allowPasswordEntry
                storeKey={APPEAL_STORE_KEY}
                defaultFiles={files}
                activeFile={previewFileIndex}
                onSetActivePage={setPreviewFileIndex}
                isActiveFileShown
                isShowTooltip={false}
                disableUpload
                isRemovingDisabled
                pdfWithoutToolbar
              />
            )}
            <Box
              background="module"
              round="container3Round"
              style={previewFileIndex !== null ? { display: 'none' } : undefined}
              pad="spacing16"
            >
              <Box pad={{ bottom: 'spacing12' }}>{descriptionTexts}</Box>
              {formContent}
            </Box>
          </Box>

          <Box direction="row" gap="spacing12" pad={{ vertical: 'spacing32', horizontal: 'spacing24' }}>
            <Box flex="grow">
              <AppButton buttonType="secondary" fill onClick={handleClose} testId="claim-appeal-cancel">
                Cancel
              </AppButton>
            </Box>
            <Box flex="grow">
              <AppButton fill onClick={handleSubmit} testId="claim-appeal-submit">
                {isLoading ? <Preloader /> : 'Submit'}
              </AppButton>
            </Box>
          </Box>
        </>
      ) : (
        <>
          <Box direction="row" align="center" justify="center">
            <Text size="2xl" color="textBody" weight="bold" textAlign="center" data-testId="claim-appeal-modal_title">
              Claim Appeal
            </Text>
          </Box>

          <Box background="module" round="moduleRound" margin={{ top: 'spacing16' }} pad="spacing24">
            <Box pad={{ bottom: 'spacing24' }}>{descriptionTexts}</Box>
            {formContent}
          </Box>

          <Box direction="row" justify="end" gap="spacing12" margin={{ top: 'spacing24' }}>
            <AppButton buttonType="secondary" width="160px" onClick={handleClose} testId="claim-appeal-cancel">
              Cancel
            </AppButton>
            <AppButton width="160px" onClick={handleSubmit} testId="claim-appeal-submit">
              {isLoading ? <Preloader /> : 'Submit'}
            </AppButton>
          </Box>
        </>
      )}

      {!isMobile && (
        <TransactionDocumentsPreviewModal
          visible={previewFileIndex !== null}
          onSetVisible={(val) => { if (!val) setPreviewFileIndex(null); }}
          modalTitle={previewFileIndex !== null ? files[previewFileIndex]?.name : undefined}
        >
          <ScanPreviewContainer
            allowPasswordEntry
            storeKey={APPEAL_STORE_KEY}
            defaultFiles={files}
            activeFile={previewFileIndex ?? 0}
            onSetActivePage={setPreviewFileIndex}
            isActiveFileShown
            isShowTooltip={false}
            disableUpload
            isRemovingDisabled
            pdfWithoutToolbar
          />
        </TransactionDocumentsPreviewModal>
      )}

      {uploadError && (
        <UploadFileErrorPopup
          fileError={uploadError}
          onClose={() => setUploadError(undefined)}
          onUploadAnotherDocument={() => {
            open();
            setUploadError(undefined);
          }}
        />
      )}
    </Modal>
  );
};

export default ClaimAppealModal;
